// Tier-specific server error handling for POST/PUT /v4/dash/campaigns.
// Maps contract error codes (see features/PLA-7637/api_contract/contract.md)
// to user-facing messages.
//
// 4221: tier-level (ProcessTiers guards) - missing rule, unsupported event,
//       tier cap, feature flag off, partner-referral with tiers.
// 4222: campaign-model (Campaign validations) - mode flip on active/inactive,
//       campaign_referral_type !== user, threshold uniqueness.
// 4002: outcome probability validation - should be unreachable client-side
//       (translator strips probability) but surfaced for safety.
// 404 (rendered as HTTP 400): cross-campaign tier id - refresh the form.

export type TierErrorCode = 4221 | 4222 | 4002 | 404;

interface TierErrorEnvelope {
  code?: number;
  message?: string;
}

const TIER_CODE_PREFIXES: Record<number, string> = {
  4221: 'Tier ladder: ',
  4222: 'Campaign: ',
  4002: 'Outcome probability: '
};

const CROSS_CAMPAIGN_TIER_MESSAGE =
  'This tier no longer exists on this campaign. Please reload the page and try again.';

const isTierCode = (code: unknown): code is TierErrorCode =>
  code === 4221 || code === 4222 || code === 4002 || code === 404;

export const isTierServerError = (error: unknown): error is TierErrorEnvelope => {
  if (!error || typeof error !== 'object') return false;
  const code = (error as TierErrorEnvelope).code;
  return isTierCode(code);
};

export const formatTierServerError = (error: TierErrorEnvelope): string => {
  if (!error?.code) return error?.message ?? '';
  if (error.code === 404) return CROSS_CAMPAIGN_TIER_MESSAGE;
  const prefix = TIER_CODE_PREFIXES[error.code] ?? '';
  return `${prefix}${error.message ?? ''}`.trim();
};
